import { isNumber } from 'lodash';
import {
  OrderActionDto,
  OrderActionIntDto,
  OrderDurationDto,
  OrderDurationIntDto,
  OrderStatusDto,
  OrderStatusIntDto,
  OrderTypeDto,
  OrderTypeIntDto,
} from 'src/dtos';

export enum OrderTypeLabel {
  Market = 'Market Order',
  Limit = 'Limit Order',
  StopMarket = 'Stop Market Order',
  StopLimit = 'Stop Limit Order',
  MarketOnClose = 'Market on Close Order',
}

export enum OrderActionLabel {
  Buy = 'Buy',
  Sell = 'Sell',
}

export enum OrderDurationLabel {
  Day = 'End of Day',
  GTC = 'Good Until Cancelled',
}

export enum OrderStatusLabel {
  PROCESSING = 'PROCESSING',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}
export class OrderStatus {
  private _value: OrderStatusDto;
  private _label: OrderStatusLabel;

  constructor(status: OrderStatusIntDto | OrderStatusDto) {
    this._value = isNumber(status) ? this.mapStatusNumberToEnum(status) : (status as OrderStatusDto);
    this._label = this.findLabel();
  }

  private mapStatusNumberToEnum(status: OrderStatusIntDto): OrderStatusDto {
    switch (status) {
      case OrderStatusIntDto.New:
        return OrderStatusDto.New;
      case OrderStatusIntDto.PartiallyFilled:
        return OrderStatusDto.PartiallyFilled;
      case OrderStatusIntDto.Filled:
        return OrderStatusDto.Filled;
      case OrderStatusIntDto.DoneForDay:
        return OrderStatusDto.DoneForDay;
      case OrderStatusIntDto.Cancelled:
        return OrderStatusDto.Cancelled;
      case OrderStatusIntDto.Replaced:
        return OrderStatusDto.Replaced;
      case OrderStatusIntDto.PendingCancel:
        return OrderStatusDto.PendingCancel;
      case OrderStatusIntDto.Stopped:
        return OrderStatusDto.Stopped;
      case OrderStatusIntDto.Rejected:
        return OrderStatusDto.Rejected;
      case OrderStatusIntDto.Suspended:
        return OrderStatusDto.Suspended;
      case OrderStatusIntDto.PendingNew:
        return OrderStatusDto.PendingNew;
      case OrderStatusIntDto.Calculated:
        return OrderStatusDto.Calculated;
      case OrderStatusIntDto.Expired:
        return OrderStatusDto.Expired;
      case OrderStatusIntDto.PendingReplace:
        return OrderStatusDto.PendingReplace;
      case OrderStatusIntDto.Saved:
        return OrderStatusDto.Saved;
      case OrderStatusIntDto.LiveUntriggered:
        return OrderStatusDto.LiveUntriggered;
      case OrderStatusIntDto.Scheduled:
        return OrderStatusDto.Scheduled;
      case OrderStatusIntDto.OCO_Untriggered:
        return OrderStatusDto.OCO_Untriggered;
      case OrderStatusIntDto.CancelledUntriggered:
        return OrderStatusDto.CancelledUntriggered;
      case OrderStatusIntDto.Initiated:
        return OrderStatusDto.Initiated;
      case OrderStatusIntDto.ReplaceInitiated:
        return OrderStatusDto.ReplaceInitiated;
      case OrderStatusIntDto.CancelInitiated:
        return OrderStatusDto.CancelInitiated;
      case OrderStatusIntDto.CancelRejected:
        return OrderStatusDto.CancelRejected;
      case OrderStatusIntDto.ReplaceRejected:
        return OrderStatusDto.ReplaceRejected;
      case OrderStatusIntDto.Busted:
        return OrderStatusDto.Busted;
      case OrderStatusIntDto.PreAllocated:
        return OrderStatusDto.PreAllocated;
      case OrderStatusIntDto.Acknowledged:
        return OrderStatusDto.Acknowledged;
      case OrderStatusIntDto.PartiallyCancelled:
        return OrderStatusDto.PartiallyCancelled;
    }
  }

  private findLabel(): OrderStatusLabel {
    switch (this._value) {
      case OrderStatusDto.New:
      case OrderStatusDto.PartiallyFilled:
      case OrderStatusDto.Calculated:
      case OrderStatusDto.DoneForDay:
      case OrderStatusDto.Saved:
      case OrderStatusDto.Acknowledged:
        return OrderStatusLabel.PROCESSING;
      case OrderStatusDto.PendingNew:
      case OrderStatusDto.PendingCancel:
      case OrderStatusDto.Scheduled:
      case OrderStatusDto.CancelInitiated:
      case OrderStatusDto.Stopped:
        return OrderStatusLabel.PENDING;
      case OrderStatusDto.Rejected:
      case OrderStatusDto.ReplaceRejected:
      case OrderStatusDto.CancelRejected:
      case OrderStatusDto.Suspended:
      case OrderStatusDto.Expired:
        return OrderStatusLabel.REJECTED;
      case OrderStatusDto.Filled:
        return OrderStatusLabel.COMPLETED;
      case OrderStatusDto.Cancelled:
      case OrderStatusDto.PartiallyCancelled:
        return OrderStatusLabel.CANCELLED;
      default:
        return OrderStatusLabel.PROCESSING;
    }
  }

  get value(): OrderStatusDto {
    return this._value;
  }

  get label(): OrderStatusLabel {
    return this._label;
  }

  get isPending() {
    return this._label === OrderStatusLabel.PENDING;
  }

  get isCompleted() {
    return this._label === OrderStatusLabel.COMPLETED;
  }

  get isCancelled() {
    return this._label === OrderStatusLabel.CANCELLED;
  }

  get isPendingNew() {
    return this._value === OrderStatusDto.PendingNew;
  }

  get isRejected() {
    return this._value === OrderStatusDto.Rejected;
  }

  get isPartiallyCancelled() {
    return this._value === OrderStatusDto.PartiallyCancelled;
  }

  isCancellable() {
    return CANCEL_ORDER_STATUS_LIST.includes(this._value);
  }
}

export class OrderType {
  private _value: OrderTypeDto;
  private _label: OrderTypeLabel;

  constructor(type: OrderTypeIntDto | OrderTypeDto) {
    this._value = isNumber(type) ? this.mapTypeNumberToEnum(type) : (type as OrderTypeDto);
    this._label = this.mapTypeEnumToLabel(this._value);
  }

  private mapTypeNumberToEnum(type: OrderTypeIntDto): OrderTypeDto {
    switch (type) {
      case OrderTypeIntDto.Market:
        return OrderTypeDto.Market;
      case OrderTypeIntDto.Limit:
        return OrderTypeDto.Limit;
      case OrderTypeIntDto.Stop_Market:
        return OrderTypeDto.StopMarket;
      case OrderTypeIntDto.Stop_Limit:
        return OrderTypeDto.StopLimit;
      case OrderTypeIntDto.Market_On_Close:
        return OrderTypeDto.MarketOnClose;
    }
  }

  private mapTypeEnumToLabel(type: OrderTypeDto): OrderTypeLabel {
    switch (type) {
      case OrderTypeDto.Market:
        return OrderTypeLabel.Market;
      case OrderTypeDto.Limit:
        return OrderTypeLabel.Limit;
      case OrderTypeDto.StopMarket:
        return OrderTypeLabel.StopMarket;
      case OrderTypeDto.StopLimit:
        return OrderTypeLabel.StopLimit;
      case OrderTypeDto.MarketOnClose:
        return OrderTypeLabel.MarketOnClose;
      default:
        return OrderTypeLabel.Market;
    }
  }

  get value(): OrderTypeDto {
    return this._value;
  }

  get label(): OrderTypeLabel {
    return this._label;
  }
}

export class OrderAction {
  private _value: OrderActionDto;
  private _label: OrderActionLabel;

  constructor(action?: OrderActionIntDto | OrderActionDto) {
    this._value = isNumber(action) ? this.mapActioNumberToEnum(action) : (action as OrderActionDto);
    this._label = this.findLabel();
  }

  private mapActioNumberToEnum(action: OrderActionIntDto): OrderActionDto {
    return action === OrderActionIntDto.Buy ? OrderActionDto.Buy : OrderActionDto.Sell;
  }

  private findLabel(): OrderActionLabel {
    return this._value === OrderActionDto.Buy ? OrderActionLabel.Buy : OrderActionLabel.Sell;
  }

  get value(): OrderActionDto {
    return this._value;
  }

  get label(): OrderActionLabel {
    return this._label;
  }

  get isBuy(): boolean {
    return this._value === OrderActionDto.Buy;
  }

  get isSell(): boolean {
    return this._value === OrderActionDto.Sell;
  }
}

export class OrderDuration {
  private _value: OrderDurationDto;
  private _label: OrderDurationLabel;

  constructor(duration: OrderDurationDto | OrderDurationIntDto) {
    this._value = isNumber(duration) ? this.mapDurationNumberToEnum(duration) : (duration as OrderDurationDto);
    this._label = this.mapDurationEnumToLabel(this._value);
  }

  private mapDurationNumberToEnum(duration: OrderDurationIntDto): OrderDurationDto {
    return duration === OrderDurationIntDto.Day ? OrderDurationDto.Day : OrderDurationDto.GTC;
  }

  private mapDurationEnumToLabel(duration: OrderDurationDto): OrderDurationLabel {
    return duration === OrderDurationDto.Day ? OrderDurationLabel.Day : OrderDurationLabel.GTC;
  }

  get value(): OrderDurationDto {
    return this._value;
  }

  get label(): OrderDurationLabel {
    return this._label;
  }
}

export interface Order {
  id: string;
  symbol: string;
  description: string;
  updatedAt: string;
  quantity: number;
  filledQuantity: number;
  isPartial: boolean;
  price: number;
  totalCost: number;
  type: OrderType;
  action: OrderAction;
  status: OrderStatus;
  duration: OrderDuration;
  commission: number;
  limit?: number;
  stop?: number;
}

// TODO: confirm pending order statuses
export const CANCEL_ORDER_STATUS_LIST = [
  OrderStatusDto.New,
  OrderStatusDto.PendingNew,
  OrderStatusDto.PendingReplace,
  OrderStatusDto.Scheduled,
  OrderStatusDto.Initiated,
  OrderStatusDto.ReplaceInitiated,
  OrderStatusDto.PartiallyFilled,
];
